import React from 'react';
import { Link } from 'react-router-dom';
import { Home, BookOpen, Play, ChevronRight, Heart, Star } from 'lucide-react';
import { bookContent } from '../data/bookContent';

export const Workshop = () => {
  const workshops = [
    {
      id: 1,
      title: "Understanding Your Trauma Response",
      description: "Learn how trauma affects your mind, body, and spirit. Discover why you respond the way you do and how to begin the journey of healing.",
      duration: "Self-paced",
      topics: [
        "Recognising trauma triggers",
        "The body-mind-spirit connection",
        "Creating your personal safety plan",
        "Grounding techniques rooted in faith"
      ],
      scripture: "Psalm 147:3 — He heals the brokenhearted and binds up their wounds.",
      reflection: "Take a moment to write down 3 situations that trigger strong emotional responses. For each one, write a scripture verse that speaks peace over that situation."
    },
    {
      id: 2,
      title: "Releasing Shame & Guilt",
      description: "A deep-dive workshop on breaking free from the shame and false guilt that survivors carry. Learn to separate what happened to you from who you are.",
      duration: "Self-paced",
      topics: [
        "The difference between shame and guilt",
        "Identifying shame triggers",
        "Replacing lies with God's truth",
        "Writing your identity declaration"
      ],
      scripture: "Romans 8:1 — Therefore, there is now no condemnation for those who are in Christ Jesus.",
      reflection: "Write a list of shame messages you've believed. Next to each one, write the truth that God's Word says about you. Read these truths aloud as declarations."
    },
    {
      id: 3,
      title: "Forgiveness & Freedom",
      description: "Explore the transformative power of forgiveness — not as letting someone off the hook, but as releasing yourself from the chains of bitterness.",
      duration: "Self-paced",
      topics: [
        "What forgiveness is and isn't",
        "Forgiving others, forgiving yourself",
        "The process of letting go",
        "Walking in daily freedom"
      ],
      scripture: "Ephesians 4:31-32 — Get rid of all bitterness, rage and anger... Be kind and compassionate to one another, forgiving each other, just as in Christ God forgave you.",
      reflection: "Write a forgiveness letter (you don't have to send it). Pour out your feelings honestly, then end with a declaration of release and freedom."
    },
    {
      id: 4,
      title: "Stepping Into Your Purpose",
      description: "Discover how God can use your story for His glory. Learn to see your pain as part of your purpose and begin walking in the calling He has for your life.",
      duration: "Self-paced",
      topics: [
        "From victim to overcomer",
        "Discovering your God-given gifts",
        "Using your story to help others",
        "Creating your purpose statement"
      ],
      scripture: "Jeremiah 29:11 — For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, plans to give you hope and a future.",
      reflection: "Write your personal purpose statement. Start with: 'God has called me to...' Include how your journey of healing has equipped you to serve others."
    }
  ];

  return (
    <div data-testid="workshop-page" className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-sage/5 py-8 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <Link to="/" className="flex items-center gap-2 text-charcoal-light hover:text-sage transition-colors">
              <Home className="w-5 h-5" />
              <span>Home</span>
            </Link>
            <Link to="/downloads" className="flex items-center gap-2 text-charcoal-light hover:text-sage transition-colors">
              <BookOpen className="w-5 h-5" />
              <span>Downloads</span>
            </Link>
          </div>
          <div className="text-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gold/10 rounded-full text-xs font-semibold text-gold uppercase tracking-wider mb-4">
              <Star className="w-3.5 h-3.5" /> Premium Workshop Access
            </span>
            <h1 className="font-heading text-4xl md:text-5xl text-charcoal mb-3">Purpose Reclaimed Workshop</h1>
            <p className="text-charcoal-light max-w-2xl mx-auto">
              A self-paced faith-based healing workshop designed to walk you deeper into freedom, purpose, and wholeness.
            </p>
            <p className="text-sm text-sage font-medium mt-3">By Janelle McGowan-Charles | Purpose Reclaimed Coaching</p>
          </div>
        </div>
      </div>

      {/* Workshop Sessions */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-8">
          {workshops.map((ws, idx) => (
            <div key={ws.id} data-testid={`workshop-session-${ws.id}`} className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {/* Session Header */}
              <div className="bg-sage/5 px-6 py-4 border-b border-sage/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-sage text-white flex items-center justify-center font-heading text-lg">
                    {ws.id}
                  </div>
                  <div>
                    <h2 className="font-heading text-xl text-charcoal">{ws.title}</h2>
                    <p className="text-xs text-charcoal-light">{ws.duration}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-5">
                <p className="text-charcoal-light">{ws.description}</p>

                {/* Topics */}
                <div>
                  <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wider mb-3">What You'll Explore</h3>
                  <ul className="grid sm:grid-cols-2 gap-2">
                    {ws.topics.map((topic, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-charcoal">
                        <ChevronRight className="w-4 h-4 text-sage flex-shrink-0 mt-0.5" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Scripture */}
                <div className="bg-gold-light/20 border-l-4 border-gold p-4 rounded-r-lg">
                  <p className="text-sm text-charcoal italic font-heading">{ws.scripture}</p>
                </div>

                {/* Reflection Exercise */}
                <div className="bg-sage/5 p-5 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="w-4 h-4 text-sage" />
                    <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wider">Workshop Reflection</h3>
                  </div>
                  <p className="text-sm text-charcoal-light">{ws.reflection}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Closing */}
        <div className="mt-12 text-center bg-sage/5 rounded-2xl p-8">
          <h2 className="font-heading text-2xl text-charcoal mb-3">You Are an Overcomer</h2>
          <p className="text-charcoal-light max-w-xl mx-auto mb-4">
            Completing this workshop is a powerful step in your healing journey. Remember, healing is not a destination — it's a daily walk with God. Keep pressing forward.
          </p>
          <p className="text-sm text-sage italic font-heading">
            "She is clothed with strength and dignity; she can laugh at the days to come." — Proverbs 31:25
          </p>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-stone-200 text-center">
          <p className="text-sm text-charcoal-light">By Janelle McGowan-Charles</p>
          <p className="text-xs text-charcoal-light/70 mt-1">Purpose Reclaimed Coaching</p>
          <p className="text-xs text-charcoal-light/60 mt-2">&copy; {new Date().getFullYear()} All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};
